import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { bookingService } from '../services/api';
import axios from 'axios';
import { FaBox, FaEnvelope, FaCheckCircle, FaTruck, FaMusic, FaMicrophone } from 'react-icons/fa';
import '../styles/Dashboard.css';
import PipelineWidget from './PipelineWidget';

function Dashboard() {
  const [bookingStats, setBookingStats] = useState(null);
  const [recentBookings, setRecentBookings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      // Alle Buchungstypen laden
      const [feierpaketResponse, djResponse, livemusikResponse] = await Promise.all([
        bookingService.getAll({ limit: 100 }),
        axios.get(`${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/dj`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        }),
        axios.get(`${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/livemusik`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        })
      ]);
      
      const feierpakete = feierpaketResponse.bookings || [];
      const djBookings = djResponse.data.bookings || [];
      const livemusikBookings = livemusikResponse.data.bookings || [];
      
      // Alle zusammenführen mit Typ-Kennzeichnung
      const allBookings = [
        ...feierpakete.map(b => ({ ...b, bookingType: 'feierpaket' })),
        ...djBookings.map(b => ({ ...b, bookingType: 'dj' })),
        ...livemusikBookings.map(b => ({ ...b, bookingType: 'livemusik' }))
      ];
      
      // Stats berechnen
      const stats = {
        total: allBookings.length,
        feierpakete: feierpakete.length,
        dj: djBookings.length,
        livemusik: livemusikBookings.length,
        neu: allBookings.filter(b => {
          const status = b.Status || b.Buchungsstatus;
          return status === 'Neu';
        }).length,
        bestaetigt: allBookings.filter(b => {
          const status = b.Status || b.Buchungsstatus;
          return status === 'Bestätigt';
        }).length,
        versendet: feierpakete.filter(b => b.Status === 'Versendet').length,
        abgeschlossen: allBookings.filter(b => {
          const status = b.Status || b.Buchungsstatus;
          return status === 'Abgeschlossen';
        }).length
      };

      setBookingStats(stats);
      setRecentBookings(allBookings.slice(0, 5)); // Neueste 5
    } catch (error) {
      console.error('Error loading dashboard:', error);
      // Fallback wenn Fehler
      setBookingStats({ total: 0, feierpakete: 0, dj: 0, livemusik: 0, neu: 0, bestaetigt: 0, versendet: 0, abgeschlossen: 0 });
      setRecentBookings([]);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="spinner"></div>;
  }

  // Status-Farbe bestimmen
  const getStatusColor = (status) => {
    const statusMap = {
      'Neu': 'neu',
      'Angefragt': 'angefragt',
      'In Bearbeitung': 'angefragt',
      'Angebot erstellt': 'angefragt',
      'Bestätigt': 'bestaetigt',
      'Versendet': 'versendet',
      'Zurück': 'zurueck',
      'Abgeschlossen': 'abgeschlossen',
      'Abgelehnt': 'zurueck'
    };
    return statusMap[status] || 'neu';
  };

  return (
    <div className="dashboard">
      <div className="dashboard-header">
        <h1>Dashboard</h1>
        <p className="text-gray">Willkommen zurück! Hier ist eine Übersicht deiner Aktivitäten.</p>
      </div>

      <PipelineWidget />

      {/* Stats Grid */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon" style={{ background: 'linear-gradient(135deg, #f40903 0%, #ff0000 100%)' }}>
            <FaBox />
          </div>
          <div className="stat-content">
            <h3>{bookingStats?.total || 0}</h3>
            <p>Buchungen gesamt</p>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon" style={{ background: 'linear-gradient(135deg, #f40903 0%, #ff0000 100%)' }}>
            <FaBox />
          </div>
          <div className="stat-content">
            <h3>{bookingStats?.feierpakete || 0}</h3>
            <p>Feierpakete 📦</p>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon" style={{ background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)' }}>
            <FaMusic />
          </div>
          <div className="stat-content">
            <h3>{bookingStats?.dj || 0}</h3>
            <p>DJ Buchungen 🎧</p>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon" style={{ background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)' }}>
            <FaMicrophone />
          </div>
          <div className="stat-content">
            <h3>{bookingStats?.livemusik || 0}</h3>
            <p>Livemusik 🎤</p>
          </div>
        </div>
      </div>

      {/* Recent Bookings */}
      <div className="dashboard-section">
        <div className="section-header flex-between">
          <h2>Aktuelle Buchungen</h2>
          <Link to="/feierpakete" className="btn btn-secondary">
            Alle anzeigen
          </Link>
        </div>

        <div className="card">
          {recentBookings.length === 0 ? (
            <p className="text-gray text-center">Noch keine Buchungen vorhanden.</p>
          ) : (
            <div className="table-responsive">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Typ</th>
                    <th>Kunde</th>
                    <th>E-Mail</th>
                    <th>Ort</th>
                    <th>Preis</th>
                    <th>Status</th>
                    <th>Eingangsdatum</th>
                  </tr>
                </thead>
                <tbody>
                  {recentBookings.map(booking => {
                    const name = booking.bookingType === 'dj' || booking.bookingType === 'livemusik'
                      ? `${booking.Vorname || ''} ${booking.Nachname || ''}`.trim()
                      : `${booking.Vorname || ''} ${booking.Nachname || ''}`.trim();
                    const location = booking.bookingType === 'dj' || booking.bookingType === 'livemusik'
                      ? booking['Event Ort']
                      : booking.Ort;
                    const status = booking.Status || booking.Buchungsstatus;
                    const preis = booking.bookingType === 'dj' || booking.bookingType === 'livemusik'
                      ? (booking['Manueller Preis aus CRM'] || booking.Gesamtpreis)
                      : booking.Preis;
                    const datum = booking.Reservierung_ablauf || booking['Eingegangen am'];
                    
                    // Typ Badge
                    let typeBadge = { text: '📦 Paket', bg: '#fee2e2', color: '#dc2626' };
                    if (booking.bookingType === 'dj') {
                      typeBadge = { text: '🎧 DJ', bg: '#e9d5ff', color: '#7c3aed' };
                    } else if (booking.bookingType === 'livemusik') {
                      typeBadge = { text: '🎤 Livemusik', bg: '#d1fae5', color: '#059669' };
                    }
                    
                    return (
                      <tr key={booking.id}>
                        <td>
                          <span style={{ 
                            padding: '0.3rem 0.6rem', 
                            borderRadius: '8px', 
                            fontSize: '0.85rem',
                            background: typeBadge.bg,
                            color: typeBadge.color,
                            fontWeight: '600'
                          }}>
                            {typeBadge.text}
                          </span>
                        </td>
                        <td>
                          <strong>{name || '-'}</strong>
                        </td>
                        <td>{booking['E-Mail'] || '-'}</td>
                        <td>{location || '-'}</td>
                        <td className="text-primary">
                          <strong>{preis ? `${preis}€` : '-'}</strong>
                        </td>
                        <td>
                          <span className={`status-badge status-${getStatusColor(status)}`}>
                            {status || 'Neu'}
                          </span>
                        </td>
                        <td>
                          {datum 
                            ? new Date(datum).toLocaleDateString('de-DE')
                            : '-'
                          }
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Quick Actions */}
      <div className="dashboard-section">
        <h2 className="mb-3">Schnellaktionen</h2>
        <div className="quick-actions">
          <Link to="/feierpakete/new" className="quick-action-card">
            <div className="quick-action-icon">📦</div>
            <h3>Neues Feierpaket</h3>
            <p>Feierpaket-Buchung anlegen</p>
          </Link>

          <Link to="/dj" className="quick-action-card">
            <div className="quick-action-icon">🎧</div>
            <h3>DJ Buchungen</h3>
            <p>DJ-Anfragen verwalten</p>
          </Link>

          <Link to="/customers" className="quick-action-card">
            <div className="quick-action-icon">👥</div>
            <h3>Kunden</h3>
            <p>Kundendaten verwalten</p>
          </Link>
        </div>
      </div>
    </div>
  );
}

export default Dashboard;
